import React, { useEffect, useState } from "react";

import { Form } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { selectCompanyId, selectUserRole } from "../../features/auth/authSlice";
import {
  fetchCategories,
  ICategory,
  selectCategories,
  selectCategoriesDict,
} from "../../features/category/categorySlice";
import { fetchLessons } from "../../features/lesson/lessonSlice";
import {
  createTopic,
  fetchTopics,
  hideModal,
  ITopic,
  selectDisplayModal,
} from "../../features/topic/topicSlice";
import { UserRole } from "../../features/users/usersSlice";
import { OTHER_CATEGORY_OPTION } from "../common/CategoryField";
import { AfterActions, DialogPopUp } from "../common/DialogPopUp";
import { MainLayout } from "../common/Layouts/Main";
import { StatusEnum } from "../common/Status";
import { TopicForm } from "./Form";
import { Prefixes } from "../prefixes";
import { FormFooter } from "../common/FormFooter";
import { cloneDeep } from "lodash";
import { getTranslations } from "../../features/translations/translationsUtils";

export const NewTopic: React.FunctionComponent = () => {
  const [restart, setRestart] = useState(true);
  const history = useHistory();
  const categoriesIds = useSelector(selectCategories);
  const categoriesDict = useSelector(selectCategoriesDict);
  const userRole = useSelector(selectUserRole);
  const companyId = useSelector(selectCompanyId);
  const [newTopic, setNewTopic] = useState<ITopic>();
  const dispatch = useDispatch();
  const [currentAction, setCurrentAction] = useState<string>("");
  const isModalVisible = useSelector(selectDisplayModal);
  const [form] = Form.useForm();


  useEffect(() => {
    dispatch(fetchTopics());
    dispatch(fetchCategories());
    dispatch(fetchLessons()); // make form ready to work
  }, [dispatch]);

  useEffect(() => {
    if (restart) {
      setNewTopic({} as ITopic);
      form.resetFields();
      form.setFieldsValue({});
      setRestart(false);
    }
  }, [restart, form]);

  const disabledSaveButton = false;

  const handleCreateOrUpdate = (afterAction: AfterActions) => {
    form
      .validateFields()
      .then((value) => {
        if (!newTopic) {
          return;
        }
        const topicCopy = cloneDeep(newTopic);
        if (topicCopy?.category === OTHER_CATEGORY_OPTION) {
          topicCopy.category = {
            name: topicCopy.categoryName || "",
          } as ICategory;
        }
        if (userRole !== UserRole.SuperAdmin) {
          if (topicCopy._id && topicCopy.companyId !== companyId) { // don't allow editing topic that don't belong to the company
            return;
          }
          topicCopy.companyId = companyId; // force to company in case of creating
        }
        if (!topicCopy.companyId) {
          topicCopy.companyId = undefined;
        }
        delete topicCopy.categoryName;
        topicCopy.status = StatusEnum.Enable;
        setCurrentAction(afterAction);
        dispatch(createTopic(topicCopy));
    })
      .catch((error) => {
        console.error("Company:React.FunctionComponent -> error", error);
      });
  };
 
  if (restart) {
    return null;
  }

  return (
    <MainLayout
      title={getTranslations('TOPIC_NEW')}
      onGoBack={() => {
        history.push(`${Prefixes.topic}`);
      }}
      breadcrumbs={[getTranslations('TOPIC_TITLE'), getTranslations('TOPIC_NEW_SHORT')]}
    >
      <DialogPopUp
        name={getTranslations('TOPIC_TITLE_SINGULAR')}
        action={getTranslations('TOPIC_CREATED')}
        visible={isModalVisible}
        onCancel={() => {
          dispatch(hideModal());
          if (currentAction === AfterActions.BACK) {
            history.push(`${Prefixes.topic}`);
            return;
          } 
          setRestart(true);
        }}
      />
      <TopicForm
        form={form}
        newTopic={newTopic}
        categories={categoriesDict}
        categoriesIds={categoriesIds}
        setNewTopic={setNewTopic}
      />
      <FormFooter
        editing={false}
        objectName={getTranslations('TOPIC_TITLE_SINGULAR')}
        onCancel={() => {
          history.push(`${Prefixes.topic}`);
        }}
        onSave={() => {
          handleCreateOrUpdate(AfterActions.BACK)
        }}
        onSaveAndCreateAnother={() => {
          handleCreateOrUpdate(AfterActions.STAY)
        }}
        disabledSave={disabledSaveButton}
      />
    </MainLayout>
  );
};
